/**
 * Frontend Security Utilities
 * Implements client-side security measures to complement backend security controls
 * Required for comprehensive security compliance per security documentation
 */

import DOMPurify from 'dompurify';

/**
 * Input Sanitization Service
 * Prevents XSS attacks by sanitizing user input on the frontend
 */
export class InputSanitizer {
  /**
   * Sanitize HTML content to prevent XSS attacks
   * Used for rich text inputs and user-generated content
   */
  static sanitizeHtml(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    // Configure DOMPurify with strict settings
    const cleanHtml = DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: [],
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
      KEEP_CONTENT: true,
    });
    
    return cleanHtml;
  }

  /**
   * Sanitize plain text input to prevent XSS
   * Used for form inputs and plain text fields
   */
  static sanitizeText(input: string): string {
    if (!input || typeof input !== 'string') return '';
    
    // Remove HTML tags and dangerous characters
    return input
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[<>'"&]/g, (char) => {
        const entities: Record<string, string> = {
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#x27;',
          '&': '&amp;',
        };
        return entities[char] || char;
      })
      .trim();
  }

  /**
   * Validate and sanitize email addresses
   */
  static sanitizeEmail(email: string): string {
    if (!email || typeof email !== 'string') return '';
    
    // Basic email validation and sanitization
    const sanitized = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(sanitized)) {
      throw new Error('Invalid email format');
    }
    
    return sanitized;
  }

  /**
   * Sanitize input with length limits
   */
  static sanitizeWithLimits(input: string, maxLength: number = 1000): string {
    if (!input || typeof input !== 'string') return '';
    
    const sanitized = this.sanitizeText(input);
    
    if (sanitized.length > maxLength) {
      throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
    }
    
    return sanitized;
  }
}

/**
 * Security Headers Validator
 * Validates that API responses include required security headers
 */
export class SecurityHeadersValidator {
  private static readonly REQUIRED_HEADERS = [
    'x-content-type-options',
    'x-frame-options',
    'x-xss-protection',
    // Only require strict-transport-security in production/HTTPS
  ];

  private static readonly PRODUCTION_HEADERS = [
    'strict-transport-security',
  ];

  /**
   * Validate security headers on API responses
   */
  static validateSecurityHeaders(response: Response): void {
    const warnings: string[] = [];
    
    // Check required headers for all environments
    this.REQUIRED_HEADERS.forEach(header => {
      if (!response.headers.get(header)) {
        warnings.push(`Missing security header: ${header}`);
      }
    });

    // Check production-only headers only in production or HTTPS
    const isSecureContext = window.location.protocol === 'https:' || import.meta.env.PROD;
    if (isSecureContext) {
      this.PRODUCTION_HEADERS.forEach(header => {
        if (!response.headers.get(header)) {
          warnings.push(`Missing security header: ${header}`);
        }
      });
    }

    if (warnings.length > 0) {
      // In development, always log warnings for debugging
      if (import.meta.env.DEV) {
        console.warn('ℹ️ API response missing security headers (development mode):', warnings);
      } 
      // In production, only log if it's a critical security issue
      else if (import.meta.env.PROD && warnings.some(w => w.includes('x-frame-options') || w.includes('x-content-type-options'))) {
        // Only log critical security headers missing in production
        console.warn('Security headers missing:', warnings.filter(w => 
          w.includes('x-frame-options') || 
          w.includes('x-content-type-options') ||
          w.includes('x-xss-protection')
        ));
      }
    }
  }

  /**
   * Check if response has CSRF token
   */
  static validateCSRFToken(response: Response): boolean {
    return !!response.headers.get('X-CSRF-Token');
  }
}

/**
 * Error Message Sanitizer
 * Prevents information disclosure through error messages
 */
export class ErrorSanitizer {
  /**
   * Sanitize error messages to prevent information disclosure
   */
  static sanitizeErrorMessage(error: any): string {
    if (!error) return 'An unexpected error occurred';
    
    // Convert error to string
    const errorMessage = typeof error === 'string' ? error : error.message || 'Unknown error';
    
    // In production, use generic messages for security
    if (import.meta.env.PROD) {
      return this.getGenericErrorMessage(errorMessage);
    }
    
    // In development, return sanitized but detailed messages
    return InputSanitizer.sanitizeText(errorMessage);
  }

  /**
   * Get generic error message for production use
   */
  private static getGenericErrorMessage(originalError: string): string {
    const lowerError = originalError.toLowerCase();
    
    // Map specific error types to generic messages
    if (lowerError.includes('network') || lowerError.includes('fetch')) {
      return 'Connection error. Please check your internet connection and try again.';
    }
    
    if (lowerError.includes('unauthorized') || lowerError.includes('401')) {
      return 'Please log in to continue.';
    }
    
    if (lowerError.includes('forbidden') || lowerError.includes('403')) {
      return 'You do not have permission to perform this action.';
    }
    
    if (lowerError.includes('not found') || lowerError.includes('404')) {
      return 'The requested resource was not found.';
    }
    
    if (lowerError.includes('rate limit') || lowerError.includes('429')) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    
    if (lowerError.includes('server') || lowerError.includes('500')) {
      return 'A server error occurred. Please try again later.';
    }
    
    // Default generic message
    return 'An error occurred while processing your request. Please try again.';
  }
}

/**
 * Form Security Helper
 * Provides security utilities for form handling
 */
export class FormSecurity {
  /**
   * Sanitize form data before submission
   */
  static sanitizeFormData(formData: Record<string, any>): Record<string, any> {
    const sanitized: Record<string, any> = {};
    
    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string') {
        // Apply appropriate sanitization based on field type
        if (key.toLowerCase().includes('email')) {
          sanitized[key] = InputSanitizer.sanitizeEmail(value);
        } else if (key.toLowerCase().includes('html') || key.toLowerCase().includes('content')) {
          sanitized[key] = InputSanitizer.sanitizeHtml(value);
        } else {
          sanitized[key] = InputSanitizer.sanitizeText(value);
        }
      } else {
        sanitized[key] = value;
      }
    });
    
    return sanitized;
  }

  /**
   * Validate form data against maximum lengths
   */
  static validateFormLimits(formData: Record<string, any>): void {
    const limits: Record<string, number> = {
      name: 100,
      email: 254,
      username: 30,
      school: 200,
      description: 5000,
      content: 10000,
    };

    Object.entries(formData).forEach(([key, value]) => {
      if (typeof value === 'string') {
        const limit = limits[key] || 1000; // Default limit
        if (value.length > limit) {
          throw new Error(`${key} exceeds maximum length of ${limit} characters`);
        }
      }
    });
  }
}

/**
 * Content Security Policy Helper
 * Provides utilities for CSP compliance
 */
export class CSPHelper {
  /**
   * Generate a nonce for inline scripts (if needed)
   */
  static generateNonce(): string {
    return btoa(crypto.getRandomValues(new Uint8Array(16)).toString());
  }

  /**
   * Check if content violates CSP policies
   */
  static validateContent(content: string): boolean {
    // Check for potentially dangerous content
    const dangerousPatterns = [
      /<script[^>]*>/i,
      /javascript:/i,
      /data:text\/html/i,
      /on\w+\s*=/i, // Event handlers
    ];

    return !dangerousPatterns.some(pattern => pattern.test(content));
  }
}

/**
 * Security Configuration
 * Central configuration for security settings
 */
export const SecurityConfig = {
  // Maximum input lengths
  MAX_LENGTHS: {
    name: 100,
    email: 254,
    username: 30,
    school: 200,
    description: 5000,
    content: 10000,
    general: 1000,
  },
  
  // Security settings
  SETTINGS: {
    enableCSRFValidation: true,
    enableHeaderValidation: true,
    enableInputSanitization: true,
    enableErrorSanitization: true,
  },
  
  // Allowed HTML tags for rich text
  ALLOWED_HTML_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
} as const; 